import React, { useState, useEffect } from "react";
import { useSession } from "../../providers/SessionProvider"; // Import useSession
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter,
  X,
  Calendar,
  ChevronDown,
  Wallet,
  RefreshCw,
  CreditCard,
  ArrowUpCircle,
  ArrowLeftRight,
  Percent,
  Receipt,
  HandHeart,
  TrendingUp,
  PiggyBank,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  ChevronRight,
  Plus,
  Minus,
  AlertCircle,
  Search,
  ShieldAlert,
  ArrowRight,
  Mail,
  CalendarDays,
  CalendarRange,
  CalendarSearch,
  Loader2,
} from "lucide-react";
import fondImageDesktop from "../../components/profileUser/assets/Fond-Large.jpg";
import fondImageMobile from "../../components/profileUser/assets/Fond.jpg";
import { comptesApi } from "../../api/clients.api";

const History = () => {
  // State for API data
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("all");

  const [selectedType, setSelectedType] = useState("all");
  const [selectedCompte, setSelectedCompte] = useState("all");
  const [filterDateRange, setFilterDateRange] = useState("all");
  const [specificDate, setSpecificDate] = useState(new Date());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showAccountFilter, setShowAccountFilter] = useState(false);
  const [showDepoSolDetails, setShowDepoSolDetails] = useState(false);
  const [depoSolDetails, setDepoSolDetails] = useState([]);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  /* REMOVE Local Fetch - Use Context Data */
  /* REMOVE Local Fetch - Use Context Data */
  /* REMOVE Local Fetch - Use Context Data */
  /* REMOVE Local Fetch - Use Context Data */
  const { userData, updateTransactionCache } = useSession();

  // Initialize accounts from userData
  useEffect(() => {
    if (userData.accounts) {
      setAccounts(userData.accounts);
    }
  }, [userData.accounts]);

  /*
  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await comptesApi.getMyAccounts();
        const accountsList = response.results || [];
        setAccounts(accountsList);
        if (accountsList.length > 0) {
          setSelectedAccountId(accountsList[0].id);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
        setError('Impossible de charger les comptes');
      }
    };
    fetchAccounts();
  }, []);
  */

  // Fetch transactions when account or filters change
  useEffect(() => {
    if (!selectedAccountId || accounts.length === 0) return;

    const fetchTransactions = async () => {
      // 1. Generate Cache Key
      let dateKey = filterDateRange;
      if (filterDateRange === 'specific' && specificDate) {
        dateKey = `${filterDateRange}-${specificDate.toISOString().split('T')[0]}`;
      }
      const cacheKey = `history-${selectedAccountId}-${selectedType}-${dateKey}`;

      // 2. Check Cache
      const cachedData = userData.transactionCache?.[cacheKey];
      if (cachedData) {
        setTransactions(cachedData);
        setLoading(false);
        return;
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        const options = {
          type: selectedType !== 'all' ? selectedType : undefined,
          group_sol: true,
        };

        // Add date filters
        if (filterDateRange !== 'all') {
          const now = new Date();

          // Helper to get local date as YYYY-MM-DD (instead of UTC from toISOString)
          const getLocalDateString = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          switch (filterDateRange) {
            case 'today':
              const todayStr = getLocalDateString(now);
              options.start_date = todayStr;
              options.end_date = todayStr;
              break;
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              options.start_date = getLocalDateString(weekAgo);
              break;
            case 'month':
              const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              options.start_date = getLocalDateString(monthAgo);
              break;
            case 'specific':
              options.start_date = getLocalDateString(specificDate);
              options.end_date = getLocalDateString(specificDate);
              break;
          }
        }

        let allTransactions = [];

        if (selectedAccountId === "all") {
          // Fetch from all accounts and merge
          const promises = accounts.map(acc =>
            comptesApi.getClientHistory(acc.id, options).catch(() => ({ results: [] }))
          );
          const results = await Promise.all(promises);
          results.forEach((res, idx) => {
            const txs = res.results || [];
            // Add account code to each transaction
            txs.forEach(tx => {
              tx.compte_code = accounts[idx]?.code || '';
            });
            allTransactions = [...allTransactions, ...txs];
          });
          // Sort by date desc
          allTransactions.sort((a, b) => {
            const dateA = new Date(a.latest_created_at || a.created_at || 0);
            const dateB = new Date(b.latest_created_at || b.created_at || 0);
            return dateB - dateA;
          });
        } else {
          // Fetch from single account
          const response = await comptesApi.getClientHistory(selectedAccountId, options);
          allTransactions = response.results || [];
        }

        setTransactions(allTransactions);
        // 3. Update Cache
        if (updateTransactionCache) {
          updateTransactionCache(cacheKey, allTransactions);
        }

      } catch (err) {
        console.error('Error fetching transactions:', err);
        // Only show error if we don't have cached data
        if (!cachedData) {
          setError('Impossible de charger les transactions');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [selectedAccountId, selectedType, filterDateRange, specificDate, accounts, userData.transactionCache, updateTransactionCache]);

  // Get available types from transactions
  const availableTypes = [...new Set(transactions.map((t) => t.type))].filter(Boolean);

  // Obtenir l'icône et la couleur par type
  const getIconByType = (type) => {
    const iconMap = {
      depo_sol: { Icon: PiggyBank, color: "#10b981" },
      depo_bs: { Icon: DollarSign, color: "#22c55e" },
      depo_solide: { Icon: Users, color: "#14b8a6" },
      depo_solidaire: { Icon: Users, color: "#14b8a6" },
      retrait: { Icon: ArrowUpCircle, color: "#ef4444" },
      withdrawal: { Icon: ArrowUpCircle, color: "#dc2626" },
      transfer: { Icon: ArrowLeftRight, color: "#3b82f6" },
      transfert: { Icon: ArrowLeftRight, color: "#2563eb" },
      depo_mu: { Icon: HandHeart, color: "#f59e0b" },
      depo_mutuelle: { Icon: HandHeart, color: "#f59e0b" },
      depo_pl: { Icon: TrendingUp, color: "#8b5cf6" },
      placement: { Icon: TrendingUp, color: "#8b5cf6" },
      commission: { Icon: Percent, color: "#06b6d4" },
      frais: { Icon: Receipt, color: "#64748b" },
      payment: { Icon: CreditCard, color: "#ec4899" },
      paiement: { Icon: CreditCard, color: "#ec4899" },
    };
    return iconMap[type] || { Icon: DollarSign, color: "#06036e" };
  };

  // Grouper par date (handles API response format)
  const groupByDate = (transactionsList) => {
    const groups = {};
    transactionsList.forEach((transaction) => {
      // API returns: latest_created_at for groups, created_at for regular
      const dateStr = transaction.latest_created_at || transaction.created_at || transaction.date;
      if (!dateStr) return;

      const date = new Date(dateStr);
      const dateKey = date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      // Normalize the transaction to match frontend expectations
      const normalizedTx = {
        ...transaction,
        // Use total_montant for grouped, montant for regular
        montant: parseFloat(transaction.total_montant || transaction.montant || 0),
        // Normalize date field
        created_at: dateStr,
        // Sub transactions array (API uses 'transactions', we expect 'sub_transactions')
        sub_transactions: transaction.transactions || transaction.sub_transactions || [],
      };

      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(normalizedTx);
    });
    return groups;
  };

  // Transactions are already filtered by API, no need for client-side filtering
  const getFilteredTransactions = () => transactions;

  const filteredTransactions = getFilteredTransactions();
  const groupedTransactions = groupByDate(filteredTransactions);

  // Gérer le clic sur une transaction groupée
  const handleTransactionClick = (transaction) => {
    if (transaction.is_group && transaction.sub_transactions) {
      setDepoSolDetails(transaction.sub_transactions);
      setShowDepoSolDetails(true);
    } else {
      setSelectedTransaction(transaction);
      setShowTransactionDetails(true);
    }
  };

  // Refresh - refetch data from API
  const handleRefresh = async () => {
    if (!selectedAccountId || refreshing) return;
    setRefreshing(true);
    try {
      const response = await comptesApi.getClientHistory(selectedAccountId, {
        type: selectedType !== 'all' ? selectedType : undefined,
        group_sol: true,
      });
      setTransactions(response.results || []);
    } catch (err) {
      console.error('Error refreshing:', err);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Image de fond fixe - Mobile */}
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
        style={{
          backgroundImage: `url(${fondImageMobile})`,
        }}
      />
      {/* Image de fond fixe - Desktop / Tablet */}
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
        style={{
          backgroundImage: `url(${fondImageDesktop})`,
        }}
      />
      {/* Header - Mobile only - Compact */}
      <div className="bg-white/80 backdrop-blur-md sticky top-0 z-40 md:hidden">
        <div className="max-w-7xl mx-auto px-4 py-2">
          {/* Title row */}
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-bold text-gray-900">Historique</h1>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {filteredTransactions.length}
              </span>
            </div>
            <motion.button
              whileTap={{ scale: 0.95, rotate: 180 }}
              onClick={handleRefresh}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 text-blue-900 ${refreshing ? "animate-spin" : ""
                  }`}
              />
            </motion.button>
          </div>

          {/* Type filters - horizontal scroll */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedType("all")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${selectedType === "all"
                ? "bg-blue-900 text-white"
                : "bg-white/70 text-gray-600 border border-gray-200"
                }`}
            >
              <Filter className="w-3 h-3" />
              Tous
            </button>

            {availableTypes.map((type) => {
              const { Icon, color } = getIconByType(type);
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${selectedType === type
                    ? "bg-blue-900 text-white"
                    : "bg-white/70 text-gray-600 border border-gray-200"
                    }`}
                >
                  <Icon
                    className="w-3 h-3"
                    style={{ color: selectedType === type ? "#fff" : color }}
                  />
                  {type.replace("_", " ")}
                </button>
              );
            })}
          </div>

          {/* Account filter - compact */}
          {accounts.length > 1 && (
            <button
              onClick={() => setShowAccountFilter(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/70 rounded-full border border-gray-200 text-xs font-medium text-gray-700 mt-2"
            >
              <Wallet className="w-3 h-3 text-blue-900" />
              <span>
                {selectedCompte === "all" ? "Tous les comptes" : `#${selectedCompte}`}
              </span>
              <ChevronDown className="w-3 h-3 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Content: Sidebar + List (Desktop), Mobile keeps flow */}
      <div className="px-6 md:px-16 lg:px-32 mt-4 md:mt-6">
        <div className="max-w-7xl mx-auto md:grid md:grid-cols-12 md:gap-6">
          {/* Sidebar Filters - Desktop */}
          <div className="hidden md:block md:col-span-4">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Filtres</h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAdvancedFilters(true)}
                  className="px-3 py-1.5 text-xs rounded-full bg-blue-900 text-white"
                >
                  Période
                </motion.button>
              </div>

              {/* Types */}
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                Type
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setSelectedType("all")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border-2 ${selectedType === "all"
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white/50 text-gray-600 border-gray-100 hover:border-blue-900"
                    }`}
                >
                  <Filter className="w-3.5 h-3.5" />
                  Tous
                </button>
                {availableTypes.map((type) => {
                  const { Icon, color } = getIconByType(type);
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border-2 ${selectedType === type
                        ? "bg-blue-900 text-white border-blue-900"
                        : "bg-white/50 text-gray-600 border-gray-100 hover:border-blue-900"
                        }`}
                    >
                      <Icon
                        className="w-3.5 h-3.5"
                        style={{
                          color: selectedType === type ? "#fff" : color,
                        }}
                      />
                      {type.replace("_", " ")}
                    </button>
                  );
                })}
              </div>

              {/* Compte */}
              {accounts.length > 1 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Compte
                  </p>
                  <button
                    onClick={() => setShowAccountFilter(true)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-white/50 rounded-full border-2 border-gray-200 hover:border-blue-900 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-blue-900" />
                      <span className="text-xs font-semibold text-gray-700">
                        {selectedCompte === "all"
                          ? "Tous les comptes"
                          : `Compte ${selectedCompte}`}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Transactions List */}
          <div className="md:col-span-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-blue-900 animate-spin mb-4" />
                <p className="text-gray-500 font-medium">Chargement de l'historique...</p>
              </div>
            ) : Object.keys(groupedTransactions).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Search className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-base font-medium text-gray-600 mb-2">
                  Aucune transaction trouvée
                </p>
                <p className="text-sm text-gray-400">
                  Essayez d'autres filtres
                </p>
              </div>
            ) : (
              Object.keys(groupedTransactions).map((dateKey, index) => (
                <motion.div
                  key={dateKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="mb-6"
                >
                  {/* Date Header */}
                  <div className="flex items-center justify-end mb-3">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border-r-4 border-blue-900">
                      <span className="text-sm font-bold text-gray-900">
                        {dateKey}
                      </span>
                      <Calendar className="w-4 h-4 text-blue-900" />
                    </div>
                  </div>

                  {/* Transactions Card */}
                  <div className="bg-white/50 rounded-xl overflow-hidden border border-gray-200">
                    {groupedTransactions[dateKey].map((transaction, idx) => {
                      const { Icon, color } = getIconByType(transaction.type);
                      const isNegative =
                        transaction.type.includes("retrait") ||
                        transaction.type.includes("withdrawal");
                      const isLast =
                        idx === groupedTransactions[dateKey].length - 1;

                      // Derive status: use transaction.status or fallback to first sub-transaction status for groups
                      const displayStatus = transaction.status ||
                        (transaction.is_group && transaction.sub_transactions && transaction.sub_transactions.length > 0
                          ? transaction.sub_transactions[0].status
                          : null);

                      const isSuccess = displayStatus === "reussi" || displayStatus === "en_cours";

                      return (
                        <motion.button
                          key={transaction.id}
                          whileHover={{ backgroundColor: "#f8fafc" }}
                          onClick={() => handleTransactionClick(transaction)}
                          className={`w-full px-4 py-3 text-left transition-colors ${!isLast ? "border-b border-gray-100" : ""
                            }`}
                        >
                          {/* Time + Status */}
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-400">
                              {(() => {
                                // Handle ISO date format
                                const dateStr = transaction.created_at;
                                if (!dateStr) return '';
                                const date = new Date(dateStr);
                                return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                              })()}
                            </span>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${isSuccess
                                ? "bg-green-50 text-green-600"
                                : "bg-red-50 text-red-600"
                                }`}
                            >
                              {isSuccess
                                ? "Réussi"
                                : "Échoué"}
                            </span>
                          </div>

                          {/* Icon + Description + Amount */}
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${color}15` }}
                            >
                              <Icon className="w-5 h-5" style={{ color }} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 mb-1">
                                {transaction.is_group
                                  ? `${transaction.count || transaction.transactions_count || ''} × ${(transaction.type || '').replace("_", " ")}`
                                  : (transaction.type || '')
                                    .replace("_", " ")
                                    .toUpperCase()}
                              </p>
                              {transaction.description && (
                                <p className="text-xs text-gray-500 mb-1 truncate">
                                  {transaction.description}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 truncate">
                                {transaction.is_group
                                  ? `Groupe: ${transaction.group_key || ''}`
                                  : `TID: ${(transaction.id || '').substring(0, 16)}...`
                                }
                              </p>
                              {transaction.created_by_username && (
                                <p className="text-[10px] font-medium text-blue-900/60 mt-1">
                                  Pa : {transaction.created_by_username}
                                </p>
                              )}
                            </div>

                            <div className="text-right">
                              <p
                                className={`text-base font-bold ${isNegative ? "text-red-500" : "text-green-500"
                                  }`}
                              >
                                {isNegative ? "-" : "+"}
                                {(Number(transaction.montant) || 0)
                                  .toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                  .replace(/,/g, " ")}
                              </p>
                              <p className="text-xs text-gray-500">
                                {transaction.devise}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bouton Flottant Filtres Avancés */}
      <motion.button
        onClick={() => setShowAdvancedFilters(true)}
        className="fixed bottom-[15%] right-6 w-14 h-14 bg-blue-900 rounded-full flex items-center justify-center z-50 md:hidden"
      >
        <Filter className="w-6 h-6 text-white" />
      </motion.button>

      {/* Modal Filtres Avancés */}
      <AnimatePresence>
        {showAdvancedFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end"
            onClick={() => setShowAdvancedFilters(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto"
            >
              {/* Handle */}
              <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-6" />

              {/* Title */}
              <h2 className="text-lg font-bold text-gray-900 text-center mb-6">
                Filtrer par période
              </h2>

              {/* Filter Options Grid */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <button
                  onClick={() => {
                    setFilterDateRange("today");
                    setShowAdvancedFilters(false);
                  }}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${filterDateRange === "today"
                    ? "bg-green-500 border-green-500 text-white"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:border-green-500"
                    }`}
                >
                  <CalendarDays className="w-6 h-6 mb-2" />
                  <span className="text-xs font-semibold">Aujourd'hui</span>
                </button>

                <button
                  onClick={() => {
                    setFilterDateRange("week");
                    setShowAdvancedFilters(false);
                  }}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${filterDateRange === "week"
                    ? "bg-blue-500 border-blue-500 text-white"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-500"
                    }`}
                >
                  <CalendarRange className="w-6 h-6 mb-2" />
                  <span className="text-xs font-semibold">7 jours</span>
                </button>

                <button
                  onClick={() => {
                    setFilterDateRange("month");
                    setShowAdvancedFilters(false);
                  }}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${filterDateRange === "month"
                    ? "bg-purple-500 border-purple-500 text-white"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-500"
                    }`}
                >
                  <Calendar className="w-6 h-6 mb-2" />
                  <span className="text-xs font-semibold">30 jours</span>
                </button>
              </div>

              {/* Date Spécifique */}
              <button
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "date";
                  input.onchange = (e) => {
                    setSpecificDate(new Date(e.target.value));
                    setFilterDateRange("specific");
                    setShowAdvancedFilters(false);
                  };
                  input.showPicker();
                }}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${filterDateRange === "specific"
                  ? "bg-orange-500 border-orange-500 text-white"
                  : "bg-gray-50 border-gray-200 text-gray-700 hover:border-orange-500"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <CalendarSearch className="w-6 h-6" />
                  <div className="text-left">
                    <p className="text-sm font-semibold">Date spécifique</p>
                    {filterDateRange === "specific" && (
                      <p className="text-xs opacity-90 mt-1">
                        {specificDate.toLocaleDateString("fr-FR")}
                      </p>
                    )}
                  </div>
                </div>
                <ChevronDown className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Sélection Compte */}
      <AnimatePresence>
        {showAccountFilter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAccountFilter(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[70vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Choisir un compte
                </h3>
                <button onClick={() => setShowAccountFilter(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    setSelectedAccountId("all");
                    setSelectedCompte("all");
                    setShowAccountFilter(false);
                  }}
                  className={`w-full p-4 rounded-lg text-left transition-colors ${selectedCompte === "all"
                    ? "bg-blue-900 text-white"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-900"
                    }`}
                >
                  <span className="font-medium">Tous les comptes</span>
                </button>

                {accounts.map((compte) => (
                  <button
                    key={compte.id}
                    onClick={() => {
                      setSelectedAccountId(compte.id);
                      setSelectedCompte(compte.code);
                      setShowAccountFilter(false);
                    }}
                    className={`w-full p-4 rounded-lg text-left transition-colors ${selectedAccountId === compte.id
                      ? "bg-blue-900 text-white"
                      : "bg-gray-50 hover:bg-gray-100 text-gray-900"
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">#{compte.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${selectedAccountId === compte.id ? "bg-white/20" : "bg-blue-100 text-blue-700"}`}>
                        {compte.type_compte || 'BS'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Détails Depo Sol Groupé */}
      <AnimatePresence>
        {showDepoSolDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end"
            onClick={() => setShowDepoSolDetails(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900">
                  Détails Depo Sol ({depoSolDetails.length})
                </h3>
                <button onClick={() => setShowDepoSolDetails(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3">
                {depoSolDetails.map((detail, idx) => (
                  <div key={detail.id || idx} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-gray-600">
                        {detail.created_at ? new Date(detail.created_at).toLocaleString('fr-FR') : detail.date_sol || ''}
                      </span>
                      <span className="text-sm font-bold text-green-600">
                        +{(Number(detail.montant) || 0).toFixed(2)} {detail.devise || 'HTG'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">TID: {detail.id || '-'}</p>
                    {detail.date_sol && (
                      <p className="text-xs font-bold text-gray-400 mt-1">
                        Date SOL: {detail.date_sol}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Détails Transaction */}
      <AnimatePresence>
        {showTransactionDetails && selectedTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTransactionDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-3">
                  {(() => {
                    const { Icon, color } = getIconByType(
                      selectedTransaction.type
                    );
                    return (
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${color}15` }}
                      >
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                    );
                  })()}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {selectedTransaction.type.replace("_", " ").toUpperCase()}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {selectedTransaction.created_at}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTransactionDetails(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Montant principal */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 mb-6">
                <p className="text-sm text-blue-900 mb-2">Montant</p>
                <p
                  className={`text-4xl font-bold ${selectedTransaction.type.includes("retrait") ||
                    selectedTransaction.type.includes("withdrawal")
                    ? "text-red-600"
                    : "text-green-600"
                    }`}
                >
                  {selectedTransaction.type.includes("retrait") ||
                    selectedTransaction.type.includes("withdrawal")
                    ? "-"
                    : "+"}
                  {selectedTransaction.montant
                    .toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    .replace(/,/g, " ")}
                </p>
                <p className="text-sm text-blue-900 mt-1">
                  {selectedTransaction.devise}
                </p>
              </div>

              {/* Détails */}
              <div className="space-y-4">
                {/* Statut */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">
                      Statut
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold px-3 py-1 rounded-full ${selectedTransaction.status === "en_cours"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                      }`}
                  >
                    {selectedTransaction.status === "en_cours"
                      ? "Réussi"
                      : "Échoué"}
                  </span>
                </div>

                {/* ID Transaction */}
                <div className="flex items-start justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">
                      ID Transaction
                    </span>
                  </div>
                  <span className="text-sm font-mono text-gray-900 text-right break-all ml-4">
                    {selectedTransaction.id}
                  </span>
                </div>

                {/* Description */}
                {selectedTransaction.description && (
                  <div className="flex items-start justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">
                        Description
                      </span>
                    </div>
                    <span className="text-sm text-gray-900 text-right ml-4">
                      {selectedTransaction.description}
                    </span>
                  </div>
                )}

                {/* Utilisateur */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">
                      Utilisateur
                    </span>
                  </div>
                  <span className="text-sm text-gray-900">
                    {selectedTransaction.username}
                  </span>
                </div>

                {/* Date et heure */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">
                      Date et heure
                    </span>
                  </div>
                  <span className="text-sm text-gray-900">
                    {selectedTransaction.created_at}
                  </span>
                </div>

                {/* Type de transaction */}
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">
                      Type
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-900">
                    {selectedTransaction.type.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3">
                <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold transition-colors">
                  Télécharger le reçu
                </button>
                <button className="flex-1 bg-blue-900 hover:bg-blue-800 text-white py-3 px-4 rounded-xl font-semibold transition-colors">
                  Partager
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default History;
